import type {
  MedicationDetail,
  MedicationProductType,
} from "./MedicationsVaccinesContext";
import type { Patient } from "../../basic-info/calculateBasicInfoCompletion";
import { getMedicationProductIdFromLabel } from "./options";

type MedicationHistoryRecord = NonNullable<
  NonNullable<Patient["medicalHistory"]>["medications"]
>[number];

export function hydrateMedications(
  medications: readonly Pick<
    MedicationHistoryRecord,
    "id" | "name" | "brand" | "frequency" | "notes"
  >[],
): {
  selectedProducts: MedicationProductType[];
  medicationDetails: MedicationDetail[];
} {
  return {
    selectedProducts: Array.from(
      new Set(
        medications.map(
          (medication) =>
            getMedicationProductIdFromLabel(medication.name) ?? "other",
        ),
      ),
    ),
    medicationDetails: medications.map((medication) => {
      const category = getMedicationProductIdFromLabel(medication.name);
      return {
        id: medication.id ?? undefined,
        category: category ?? "other",
        name: medication.name ?? "",
        brand: medication.brand ?? null,
        frequency: medication.frequency ?? null,
        notes: medication.notes ?? null,
      };
    }),
  };
}

export function removeMedicationDetail(
  selectedProducts: readonly MedicationProductType[],
  medicationDetails: readonly MedicationDetail[],
  medicationIdsToDelete: readonly string[],
  index: number,
): {
  selectedProducts: MedicationProductType[];
  medicationDetails: MedicationDetail[];
  medicationIdsToDelete: string[];
} {
  const medicationToRemove = medicationDetails[index];
  if (!medicationToRemove) {
    return {
      selectedProducts: [...selectedProducts],
      medicationDetails: [...medicationDetails],
      medicationIdsToDelete: [...medicationIdsToDelete],
    };
  }

  const nextDetails = medicationDetails.filter(
    (_, detailIndex) => detailIndex !== index,
  );
  const removedOther =
    medicationToRemove.category == null ||
    medicationToRemove.category === "other";
  const hasOtherDetails = nextDetails.some(
    (detail) => detail.category == null || detail.category === "other",
  );

  return {
    selectedProducts:
      removedOther && !hasOtherDetails
        ? selectedProducts.filter((product) => product !== "other")
        : [...selectedProducts],
    medicationDetails: nextDetails,
    medicationIdsToDelete: medicationToRemove.id
      ? Array.from(new Set([...medicationIdsToDelete, medicationToRemove.id]))
      : [...medicationIdsToDelete],
  };
}

export function reconcileMedicationDetails(
  selectedProducts: readonly MedicationProductType[],
  medicationDetails: readonly MedicationDetail[],
  otherMedicationText = "",
): {
  medicationDetails: MedicationDetail[];
  droppedPersistedIds: string[];
  consumedOtherMedicationText: boolean;
} | null {
  const selected = selectedProducts.filter((product) => product !== "none");
  if (selected.length === 0) return null;

  let nextDetails = [...medicationDetails];
  let changed = false;
  let consumedOtherMedicationText = false;

  selected.forEach((product) => {
    const hasDetail = nextDetails.some(
      (detail) =>
        detail.category === product ||
        (product === "other" && detail.category == null),
    );
    if (!hasDetail) {
      nextDetails.push({
        category: product,
        name: "",
        brand: null,
        frequency: null,
        notes: null,
      });
      changed = true;
    }
  });

  if (otherMedicationText && selected.includes("other")) {
    const otherIndex = nextDetails.findIndex(
      (detail) => detail.category === "other" || detail.category == null,
    );
    if (otherIndex !== -1) {
      nextDetails = nextDetails.map((detail, index) =>
        index === otherIndex
          ? {
              ...detail,
              notes: detail.notes
                ? `${detail.notes}\n${otherMedicationText}`
                : otherMedicationText,
            }
          : detail,
      );
      changed = true;
      consumedOtherMedicationText = true;
    }
  }

  // Deselecting a category must delete its persisted record, not just remove
  // the local row, or the server retains the medication.
  const droppedPersistedIds: string[] = [];
  nextDetails = nextDetails.filter((detail) => {
    if (!detail.category) return true;
    if ((selected as readonly string[]).includes(detail.category)) return true;
    if (detail.id) droppedPersistedIds.push(detail.id);
    return false;
  });

  if (nextDetails.length !== medicationDetails.length) changed = true;

  return changed
    ? {
        medicationDetails: nextDetails,
        droppedPersistedIds,
        consumedOtherMedicationText,
      }
    : null;
}
